import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { API_BASE_URL } from '../utils/api';

const GA4_MEASUREMENT_ID = 'G-QP0ZTR0VPR';

// ─── YouTube embed (watch, youtu.be, shorts) ───────────────────────────────────
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  let videoId = '';
  try {
    if (url.includes('youtube.com/watch?v=')) videoId = url.split('v=')[1].split('&')[0];
    else if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1].split('?')[0];
    else if (url.includes('youtube.com/shorts/')) videoId = url.split('shorts/')[1].split('?')[0];
  } catch (e) { return null; }
  return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=1&loop=1&playlist=${videoId}` : null;
}

// ─── Ad Screen: prioritate video local (localVideoUrl), apoi YouTube (adVideoUrl)
function PublicAdScreen({ config, onFinish }) {
  const c = config || {};
  const [seconds, setSeconds] = useState(c.adDuration ?? 15);
  const [canSkip, setCanSkip] = useState(false);
  const adDuration = c.adDuration ?? 15;

  useEffect(() => {
    if (seconds <= 0) { onFinish(); return; }
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, onFinish]);

  useEffect(() => {
    if (seconds <= adDuration - 5) setCanSkip(true);
  }, [seconds, adDuration]);

  const isVideo = c.adType === 'video';
  const activeLocalVideoUrl = c.localVideoUrl && String(c.localVideoUrl).trim() ? c.localVideoUrl.trim() : null;
  const embedUrl = isVideo && c.adVideoUrl ? getYouTubeEmbedUrl(c.adVideoUrl) : null;

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        position: 'relative',
        background: c.adBgColor || '#0F172A',
        fontFamily: 'Segoe UI, sans-serif',
      }}
    >
      {isVideo ? (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, background: 'black' }}>
          {activeLocalVideoUrl ? (
            <video
              src={activeLocalVideoUrl}
              controls
              autoPlay
              muted
              playsInline
              loop
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              title="Video Reclamă"
            />
          ) : embedUrl ? (
            <iframe
              src={embedUrl}
              title="Video Ad"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 14 }}>
              {c.adVideoUrl ? 'Link YouTube invalid sau încarcă un MP4.' : 'Aștept link YouTube sau încarcă un MP4...'}
            </div>
          )}
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center py-12 px-6 text-center">
          <div className="text-5xl mb-4">📣</div>
          <div className="text-2xl md:text-3xl font-extrabold text-white leading-tight mb-3">
            {c.adText || 'Textul reclamei tale apare aici'}
          </div>
          {c.adSubtext && <div className="text-sm text-white/70 mb-6">{c.adSubtext}</div>}
          {c.adUrl && (
            <a
              href={c.adUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 rounded-lg text-white font-bold text-sm"
              style={{ background: c.culoare }}
            >
              Află mai mult →
            </a>
          )}
        </div>
      )}
      <div style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6, zIndex: 10 }}>
        <span style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>{seconds}</span>
        {canSkip && (
          <button type="button" onClick={onFinish} className="text-white/90 hover:text-white font-semibold cursor-pointer">
            Sari →
          </button>
        )}
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: 'rgba(255,255,255,0.15)', zIndex: 10 }}>
        <div
          style={{
            height: '100%',
            width: `${((adDuration - seconds) / adDuration) * 100}%`,
            transition: 'width 1s linear',
            background: c.culoare || '#2563EB',
          }}
        />
      </div>
    </div>
  );
}

const PublicLandingPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // ref/amb: doar pentru salvare la submit; NU modifică stage — vizitorul vede mereu Hero mai întâi
  const refParam = searchParams.get('ref');
  const ambFromUrl = searchParams.get('amb');

  const [landingPage, setLandingPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Flux ca în LPPreview: 'lp' | 'ad' | 'gdpr' | 'form'. GDPR și form DOAR după click CTA.
  const [stage, setStage] = useState('lp');
  const [formData, setFormData] = useState({});
  const [customData, setCustomData] = useState({});
  const [files, setFiles] = useState([]);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [smsStep, setSmsStep] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [smsCode, setSmsCode] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsError, setSmsError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successLeadId, setSuccessLeadId] = useState(null);
  const [successAmbassadorTarget, setSuccessAmbassadorTarget] = useState(3);
  const [successAmbassadorReward, setSuccessAmbassadorReward] = useState('');
  const [ambassadorCode, setAmbassadorCode] = useState(null);
  const [ambassadorQrCode, setAmbassadorQrCode] = useState(null);
  const [ambassadorReferralLink, setAmbassadorReferralLink] = useState(null);
  const [becomeAmbassadorLoading, setBecomeAmbassadorLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const formRef = useRef(null);

  useEffect(() => {
    ReactGA.initialize(GA4_MEASUREMENT_ID);
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + (window.location.search || '') });
  }, []);

  // La mount: Hero (nu GDPR/form)
  useEffect(() => {
    setStage(s => (s === 'gdpr' || s === 'form' ? 'lp' : s));
  }, []);

  // Înainte de paint: când loading devine false, forțăm stage=lp/ad (nu gdpr/form)
  useLayoutEffect(() => {
    if (!loading) setStage(s => (s === 'gdpr' || s === 'form' ? 'lp' : s));
  }, [loading]);

  useEffect(() => {
    const fetchLandingPage = async () => {
      try {
        const statusSlug = encodeURIComponent(slug);
        const url = refParam
          ? `${API_BASE_URL}/api/public/lp/${statusSlug}?ref=${refParam}`
          : `${API_BASE_URL}/api/public/lp/${statusSlug}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          mode: 'cors'
        });

        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'Pagina nu a fost găsită');
          return;
        }

        const data = await response.json();
        const lp = data.landingPage;
        setLandingPage(lp);

        // Forțare stare vizuală: mereu Hero sau Ad la încărcare; formular doar la click CTA
        let cfg = {};
        try {
          if (lp?.designConfig) {
            cfg = typeof lp.designConfig === 'string' ? JSON.parse(lp.designConfig) : lp.designConfig;
            if (!cfg || typeof cfg !== 'object') cfg = {};
          }
        } catch (_) { cfg = {}; }
        setStage(cfg.layout === 'ad' ? 'ad' : 'lp');

        const campuri = Array.isArray(lp.campuriFormular)
          ? lp.campuriFormular
          : JSON.parse(lp.campuriFormular || '[]');

        const initialData = { nume: '', telefon: '', email: '' };
        campuri.forEach(camp => {
          const id = typeof camp === 'object' ? camp.id : camp;
          if (id && !['nume', 'telefon', 'email'].includes(String(id).toLowerCase())) {
            initialData[id] = '';
          }
        });
        setFormData(initialData);

        // Initialize custom fields
        const customFields = lp.customFields
          ? (typeof lp.customFields === 'string' ? JSON.parse(lp.customFields) : lp.customFields)
          : [];

        const initialCustomData = {};
        customFields.forEach(field => {
          initialCustomData[field.label] = '';
        });
        setCustomData(initialCustomData);

      } catch (err) {
        setError('Eroare la încărcarea paginii: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLandingPage();
    // Doar slug: ref/amb nu trebuie să declanșeze re-fetch sau să modifice stage
  // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams/ref nu trebuie în deps (evită auto-skip la Hero)
  }, [slug]);

  // Titlu dinamic pentru tab (GA4 unifiedScreenName + SEO) și og:title/og:description (share WhatsApp/Facebook)
  const DEFAULT_TITLE = 'Lead Collection Platform';
  useEffect(() => {
    if (!landingPage) {
      document.title = DEFAULT_TITLE;
      return;
    }
    const titlu = (landingPage.titlu || '').trim() || DEFAULT_TITLE;
    document.title = titlu;

    const descriere = (landingPage.descriere || '').trim();
    const canonicalUrl = window.location.origin + window.location.pathname + (window.location.search || '');

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value || '');
    };
    setMeta('property', 'og:title', titlu);
    setMeta('property', 'og:description', descriere || titlu);
    setMeta('property', 'og:url', canonicalUrl);

    // GA4: trimitem explicit titlul și path-ul după ce LP e încărcat (evită timing: page_view nu mai folosește doar document.title de la load)
    const pagePath = `/lp/${landingPage.slug}${window.location.search ? window.location.search : ''}`;
    if (typeof window.gtag === 'function') {
      window.gtag('config', GA4_MEASUREMENT_ID, {
        page_title: titlu,
        page_path: `/lp/${landingPage.slug}`,
      });
      window.gtag('event', 'page_view', {
        page_title: titlu,
        page_path: `/lp/${landingPage.slug}`,
        page_location: window.location.origin + pagePath,
      });
    } else {
      ReactGA.send({ hitType: 'pageview', page: pagePath, title: titlu });
    }

    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [landingPage]);

  // Inject Tracking Scripts (Facebook Pixel & Google Analytics)
  useEffect(() => {
    // 1. Facebook Pixel (doar când avem LP)
    if (landingPage?.facebookPixelId && !document.getElementById('fb-pixel-script')) {
      const pixelId = landingPage.facebookPixelId;
      const fbScript = document.createElement('script');
      fbScript.id = 'fb-pixel-script';
      fbScript.async = true;
      fbScript.src = 'https://connect.facebook.net/en_US/fbevents.js';
      fbScript.onload = () => {
        if (typeof window.fbq === 'function') {
          window.fbq('init', pixelId);
          window.fbq('track', 'PageView');
        }
      };
      document.head.appendChild(fbScript);
      console.log('✅ FB Pixel Injected:', pixelId);
    }

    // Cleanup on unmount
    return () => {
      const fb = document.getElementById('fb-pixel-script');
      if (fb) fb.remove();
    };
  }, [landingPage]);

  const handleCTAClick = () => {
    // Moved GDPR check to form submit
    setStage('gdpr');
  };

  const handleGdprContinue = () => {
    if (gdprConsent) {
      setStage('form');
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitError(null);

    if (!gdprConsent) {
      setSubmitError('Te rugăm să accepți politica GDPR pentru a continua.');
      setSubmitLoading(false);
      return;
    }

    // Validation
    if (!formData.nume || formData.nume.trim().length < 2) {
      setSubmitError('Numele este obligatoriu (minim 2 caractere).');
      setSubmitLoading(false);
      return;
    }

    if (!formData.telefon || !/^(\+40|0)?7\d{8}$/.test(formData.telefon.replace(/\s|-|\./g, ''))) {
      setSubmitError('Număr de telefon invalid.');
      setSubmitLoading(false);
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setSubmitError('Adresă de email invalidă.');
      setSubmitLoading(false);
      return;
    }

    try {
      const submitData = new FormData();
      if (refParam) submitData.append('ref', refParam);
      if (ambFromUrl) submitData.append('referredByCode', ambFromUrl);

      // Append standard fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });

      const extraFromForm = {};
      getCampuriExtra().forEach(c => { extraFromForm[c.id] = formData[c.id] || ''; });
      const mergedCustomData = { ...extraFromForm, ...customData };
      submitData.append('customData', JSON.stringify(mergedCustomData));
      submitData.append('gdprConsent', gdprConsent);

      // Append files
      files.forEach(file => {
        submitData.append('attachments', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/public/lp/${slug}/submit`, {
        method: 'POST',
        // No Content-Type header when using FormData, browser sets it with boundary
        body: submitData
      });

      const data = await response.json();

      if (!response.ok) {
        setSubmitError(data.error || 'Eroare la trimitere');
        return;
      }

      if (data.requiresValidation) {
        setLeadId(data.leadId);
        setSmsStep(true);
      } else {
        setSuccess(true);
      }

    } catch (err) {
      setSubmitError('Eroare la trimitere. Încearcă din nou.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSmsVerify = async (e) => {
    e.preventDefault();
    setSmsLoading(true);
    setSmsError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/public/lp/${slug}/verify-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId, cod: smsCode })
      });

      const data = await response.json();

      if (!response.ok) {
        setSmsError(data.error || 'Cod incorect');
        return;
      }

      setSuccessLeadId(data.leadId);
      setSuccessAmbassadorTarget(data.ambassadorTarget ?? 3);
      setSuccessAmbassadorReward(data.ambassadorReward ?? '');
      setSuccess(true);

    } catch (err) {
      setSmsError('Eroare la verificare. Încearcă din nou.');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleBecomeAmbassador = async () => {
    if (!successLeadId) return;
    setBecomeAmbassadorLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/become-ambassador`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId: successLeadId })
      });
      const data = await response.json();
      if (data.success && data.ambassadorCode) {
        setAmbassadorCode(data.ambassadorCode);
        if (data.qrCode) setAmbassadorQrCode(data.qrCode);
        if (data.referralLink) setAmbassadorReferralLink(data.referralLink);
      }
    } catch (err) {
      console.error('Become ambassador error:', err);
    } finally {
      setBecomeAmbassadorLoading(false);
    }
  };

  const ambassadorLink = ambassadorReferralLink || (ambassadorCode
    ? `${window.location.origin}${window.location.pathname}?amb=${encodeURIComponent(ambassadorCode)}`
    : '');
  const copyAmbassadorLink = () => {
    if (!ambassadorLink) return;
    navigator.clipboard.writeText(ambassadorLink).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  const getBulletPoints = () => {
    if (!landingPage?.bulletPoints) return [];
    try {
      // If it's already an array, return it (handle both cases)
      if (Array.isArray(landingPage.bulletPoints)) return landingPage.bulletPoints;
      // If it's a string, split by newline if distinct lines, or JSON parse if it's a JSON string
      // The updated create modal sends a newline-separated string.
      // Let's try splitting by newline first if it's a plain string that doesn't look like JSON
      if (landingPage.bulletPoints.startsWith('[')) {
        return JSON.parse(landingPage.bulletPoints);
      }
      return landingPage.bulletPoints.split('\n').filter(Boolean);
    } catch {
      return [landingPage.bulletPoints];
    }
  };

  const formatLabel = (raw) => {
    if (!raw) return '';
    const s = String(raw).replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const STANDARD_IDS = ['nume', 'telefon', 'email', 'nume_complet', 'prenume'];

  const getStandardFields = () => [
    { id: 'nume', label: 'Nume complet', tip: 'text', obligatoriu: true },
    { id: 'telefon', label: 'Telefon', tip: 'tel', obligatoriu: true },
    { id: 'email', label: 'Email', tip: 'email', obligatoriu: false }
  ];

  const getCampuriExtra = () => {
    if (!landingPage?.campuriFormular) return [];
    try {
      const campuri = Array.isArray(landingPage.campuriFormular)
        ? landingPage.campuriFormular
        : JSON.parse(landingPage.campuriFormular);
      return campuri
        .map(camp => {
          const id = typeof camp === 'string' ? camp : camp.id;
          return typeof camp === 'string'
            ? { id, label: formatLabel(id), tip: 'text', obligatoriu: false }
            : { ...camp, label: formatLabel(camp.label || camp.id) };
        })
        .filter(c => c.id && !STANDARD_IDS.includes(String(c.id).toLowerCase().replace(/\s+/g, '_')));
    } catch {
      return [];
    }
  };

  const getCustomFields = () => {
    if (!landingPage?.customFields) return [];
    try {
      return typeof landingPage.customFields === 'string'
        ? JSON.parse(landingPage.customFields)
        : landingPage.customFields;
    } catch {
      return [];
    }
  };

  const getDesignConfig = () => {
    const defaults = { font: 'Inter', formPosition: 'right', layout: 'classic', adVideoUrl: '' };
    if (!landingPage?.designConfig) return defaults;
    try {
      const raw = landingPage.designConfig;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed && typeof parsed === 'object') {
        return { ...defaults, ...parsed };
      }
      return defaults;
    } catch {
      return defaults;
    }
  };

  // Unified config for layout + ad + culoare (din DB: designConfig conține adVideoUrl; localVideoUrl din LP)
  const getLpConfig = () => {
    const designConfig = getDesignConfig();
    const culoare = landingPage?.culoareButonCTA || '#003781';
    const adVideoUrl = (designConfig.adVideoUrl != null && String(designConfig.adVideoUrl).trim()) ? String(designConfig.adVideoUrl).trim() : '';
    const localVideoUrl = (landingPage?.localVideoUrl && String(landingPage.localVideoUrl).trim()) ? String(landingPage.localVideoUrl).trim() : '';
    return {
      layout: designConfig.layout || 'classic',
      culoare,
      adType: designConfig.adType || 'banner',
      adDuration: Math.min(50, Math.max(3, Number(designConfig.adDuration) || 15)),
      adText: designConfig.adText || '',
      adSubtext: designConfig.adSubtext || '',
      adUrl: designConfig.adUrl || '',
      adVideoUrl,
      localVideoUrl,
      adBgColor: designConfig.adBgColor || '#0F172A',
    };
  };

  // Când LP se schimbă: forțăm stage=lp sau ad (niciodată gdpr/form în useEffect)
  useEffect(() => {
    if (!landingPage) return;
    const cfg = getDesignConfig();
    setStage(cfg?.layout === 'ad' ? 'ad' : 'lp');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getDesignConfig depends on landingPage
  }, [landingPage]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center p-10 bg-white rounded-2xl shadow-xl max-w-md mx-4">
          <div className="text-6xl mb-6">😕</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Pagina nu a fost găsită</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const lpConfig = getLpConfig();
  const culoareCTA = lpConfig.culoare;
  const culoareFundal = landingPage?.culoareFundal || '#FFFFFF';
  const culoareTitlu = landingPage?.culoareTextTitlu || '#212121';
  const culoareDescriere = landingPage?.culoareTextDescriere || '#757575';
  const bulletPoints = getBulletPoints();
  const standardFields = getStandardFields();
  const campuriExtra = getCampuriExtra();
  const allCustomFields = getCustomFields();
  const extraIds = campuriExtra.map(c => String(c.id).toLowerCase().replace(/\s+/g, '_'));
  const customFields = allCustomFields.filter(f => {
    const slug = String(f.label || '').toLowerCase().replace(/\s+/g, '_');
    return !extraIds.includes(slug) && !STANDARD_IDS.some(s => slug.includes(s.replace(/_/g, '')));
  });

  // Normalizare tip input: Editor folosește "type", alte surse "tip"; forțează datetime-local/date/time pentru calendar
  const normalizeInputType = (raw) => {
    const r = String(raw ?? '').trim().toLowerCase();
    if (r === 'datetime-local' || r === 'date' || r === 'time') return r;
    if (r === 'datetime' || r === 'datetime_local' || r === 'datetime local') return 'datetime-local';
    return r || 'text';
  };

  const designConfig = getDesignConfig();
  const fontFamily = designConfig.font || 'Inter';

  // Dacă layout-ul e "ad" și suntem în etapa de reclamă, rândăm doar AdScreen (config complet din DB)
  if (stage === 'ad') {
    return (
      <PublicAdScreen
        config={lpConfig || {}}
        onFinish={() => setStage('lp')}
      />
    );
  }

  if (success) {
    const target = successAmbassadorTarget ?? 3;
    const reward = successAmbassadorReward || 'o recompensă specială';
    return (
      <div className="min-h-screen flex justify-center items-center p-4 transition-colors duration-500" style={{ backgroundColor: culoareFundal, fontFamily }}>
        <div className="text-center p-8 md:p-12 bg-white rounded-3xl shadow-2xl max-w-lg w-full transform transition-all">
          <div className="text-7xl mb-6 animate-bounce">🎉</div>
          <h2 className="text-3xl font-bold mb-4" style={{ color: culoareTitlu }}>Cererea ta a fost trimisă!</h2>
          <p className="text-lg mb-6 leading-relaxed" style={{ color: culoareDescriere }}>
            {(landingPage?.agent?.numeComplet || landingPage?.op?.numeComplet)
              ? `${landingPage.agent ? 'Agentul' : 'Managerul'} ${landingPage.agent?.numeComplet || landingPage.op?.numeComplet} te va contacta în cel mai scurt timp!`
              : 'Vei fi contactat în cel mai scurt timp!'}
          </p>

          {landingPage?.allowAmbassadors !== false && !ambFromUrl && (
            !ambassadorCode ? (
              <>
                <p className="text-lg font-semibold mb-4" style={{ color: culoareTitlu }}>
                  Vrei să devii Ambasadorul nostru? Recomandă serviciile noastre la <strong>{target}</strong> prieteni și primești automat <strong>{reward}</strong>!
                </p>
                <button
                  type="button"
                  onClick={handleBecomeAmbassador}
                  disabled={becomeAmbassadorLoading}
                  className="inline-block px-8 py-4 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 disabled:opacity-70"
                  style={{ backgroundColor: culoareCTA }}
                >
                  {becomeAmbassadorLoading ? 'Se înregistrează...' : 'Vreau să devin Ambasador'}
                </button>
              </>
            ) : (
              <>
                <p className="text-lg font-semibold mb-3" style={{ color: culoareTitlu }}>Linkul tău unic de Ambasador:</p>
                <div className="flex flex-col sm:flex-row gap-2 items-center justify-center mb-4">
                  <input
                    type="text"
                    readOnly
                    value={ambassadorLink}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm font-mono bg-gray-50 text-gray-800"
                  />
                  <button
                    type="button"
                    onClick={copyAmbassadorLink}
                    className="whitespace-nowrap px-6 py-3 rounded-xl font-bold text-white shadow-md hover:shadow-lg transition-all"
                    style={{ backgroundColor: culoareCTA }}
                  >
                    {copiedLink ? '✓ Copiat!' : 'Copiază'}
                  </button>
                </div>
                {ambassadorQrCode && (
                  <div className="mb-4">
                    <img src={ambassadorQrCode} alt="Cod QR Ambasador" className="mx-auto mt-4 w-48 h-48 rounded-lg shadow-md" />
                  </div>
                )}
                <p className="text-sm mb-1" style={{ color: culoareDescriere }}>Trimite acest link prietenilor; când completează formularul, recomandarea va fi înregistrată la tine.</p>
                <p className="text-sm font-medium" style={{ color: culoareTitlu }}>Ți-am trimis link-ul și pe Email/SMS ca să îl ai mereu la îndemână!</p>
              </>
            )
          )}

          {(landingPage?.agent?.telefon || landingPage?.op?.telefon) && (
            <a
              href={`tel:${landingPage.agent?.telefon || landingPage.op?.telefon}`}
              className="mt-8 inline-block px-6 py-3 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
              style={{ backgroundColor: culoareCTA }}
            >
              📞 Sună: {landingPage.agent?.telefon || landingPage.op?.telefon}
            </a>
          )}
        </div>
      </div>
    );
  }

  // --- LAYOUT HELPERS ---

  const renderAgentBadge = (absolute = false) => {
    if (!landingPage?.agent && !landingPage?.op) return null;

    const badgePos = landingPage?.designConfig?.badgePosition || 'top-left';
    let positionClass = '';

    if (absolute) {
      positionClass = 'absolute z-10 hidden lg:flex ';
      switch (badgePos) {
        case 'top-left': positionClass += 'top-8 left-8'; break;
        case 'top-right': positionClass += 'top-8 right-8'; break;
        case 'bottom-left': positionClass += 'bottom-8 left-8'; break;
        case 'bottom-right': positionClass += 'bottom-8 right-8'; break;
        case 'center': positionClass += 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'; break;
        default: positionClass += 'top-8 left-8';
      }
    } else {
      // Relative: Visible on mobile (lg:hidden) OR always visible if layout forces it (e.g. minimalist)
      // But for split-screen we want it only on mobile in this slot.
      // We'll handle visibility in the parent or via a prop if needed.
      // For now, let's default to relative styling, parent controls visibility via wrapper or we add lg:hidden here if strictly split-screen?
      // Let's keep it generic and control visibility where called.
      positionClass = 'flex relative mb-8 mx-auto md:mx-0';
    }

    return (
      <div className={`${positionClass} items-center p-6 bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 max-w-sm transform transition-transform hover:scale-105 duration-300 ring-1 ring-gray-100`}>
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-2xl text-white mr-5 shadow-lg relative overflow-hidden"
          style={{ backgroundColor: culoareCTA }}
        >
          {((landingPage.agent?.avatarUrl) || (landingPage.op?.avatarUrl)) ? (
            <img
              src={`${API_BASE_URL}${landingPage.agent?.avatarUrl || landingPage.op?.avatarUrl}`}
              alt="Avatar"
              className="w-full h-full object-cover border-2 border-white"
            />
          ) : (
            <>
              <div className="absolute inset-0 bg-black/10"></div>
              <span className="relative">{(landingPage.agent?.numeComplet || landingPage.op?.numeComplet || 'A').charAt(0).toUpperCase()}</span>
            </>
          )}
        </div>
        <div className="flex-1 text-left">
          <p className="font-bold text-gray-900 leading-tight text-lg">
            {landingPage.agent?.numeComplet || landingPage.op?.numeComplet}
          </p>
          <p className="text-sm font-medium text-gray-500">
            {landingPage.agent ? 'Agent Asigurări' : 'Manager Agenți'}
          </p>
        </div>
        {(landingPage.agent?.telefon || landingPage.op?.telefon) && (
          <div className="ml-4 flex gap-3">
            <a
              href={`https://wa.me/${(landingPage.agent?.telefon || landingPage.op?.telefon).replace(/\D/g, '').replace(/^0/, '40')}?text=${encodeURIComponent('Buna ziua, sunt interesat de oferta...')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-xl text-white hover:opacity-90 transition-all shadow-md hover:shadow-lg flex items-center justify-center bg-gradient-to-br from-green-400 to-green-600 active:scale-95"
              title="Trimite mesaj pe WhatsApp"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
            <a
              href={`tel:${landingPage.agent?.telefon || landingPage.op?.telefon}`}
              className="p-3 rounded-xl text-white hover:opacity-90 transition-all shadow-md hover:shadow-lg flex items-center justify-center active:scale-95 relative overflow-hidden"
              style={{ backgroundColor: culoareCTA }}
              title="Sună Acum"
            >
              <div className="absolute inset-0 bg-white/20 hover:bg-transparent transition-colors"></div>
              <span className="text-xl">📞</span>
            </a>
          </div>
        )}
      </div>
    );
  };

  const renderContent = (centered = false) => (
    <>
      {!centered && <div className="lg:hidden">{renderAgentBadge(false)}</div>}

      {centered && landingPage?.designConfig?.layout === 'minimalist' && (
        <div className="mb-8 flex justify-center">{renderAgentBadge(false)}</div>
      )}

      {stage === 'form' ? (
        <div className="flex flex-col animate-fade-in transition-all duration-500 w-full">
          <button
            onClick={() => setStage('gdpr')}
            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-2 mb-6 w-fit transition-colors font-medium"
          >
            ← Înapoi la descriere
          </button>

          {!smsStep ? (
            <div ref={formRef} className={`bg-white/95 backdrop-blur-xl rounded-3xl p-8 md:p-10 shadow-2xl animate-fade-in-up border border-white/50 ring-1 ring-gray-100 ${centered ? 'max-w-2xl mx-auto' : ''}`}>
              <h3 className="text-3xl font-bold mb-8 flex items-center" style={{ color: culoareTitlu }}>
                <span className="mr-3 p-2 bg-brand-light rounded-xl text-2xl">📋</span>
                {landingPage?.titluFormular || 'Completează datele:'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                {[...standardFields, ...campuriExtra].map((camp) => (
                  <div key={camp.id} className="group relative">
                    <label className="block text-sm font-bold mb-2 ml-1" style={{ color: culoareTitlu }}>
                      {camp.label}
                      {camp.obligatoriu && <span className="text-red-500"> *</span>}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                        {camp.label.toLowerCase().includes('telefon') ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                        ) : camp.label.toLowerCase().includes('email') ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                        )}
                      </div>
                      <input
                        type={normalizeInputType(
                          (() => {
                            const slugCamp = String(camp.id || camp.label || '').toLowerCase().replace(/\s+/g, '_');
                            const customDef = allCustomFields.find(f => String(f.label || '').toLowerCase().replace(/\s+/g, '_') === slugCamp);
                            return customDef ? (customDef.type ?? customDef.tip) : (camp.tip ?? 'text');
                          })()
                        )}
                        required={camp.obligatoriu}
                        value={formData[camp.id] || ''}
                        onChange={e => setFormData({ ...formData, [camp.id]: e.target.value })}
                        placeholder={(() => {
                          const slugCamp = String(camp.id || camp.label || '').toLowerCase().replace(/\s+/g, '_');
                          const customDef = allCustomFields.find(f => String(f.label || '').toLowerCase().replace(/\s+/g, '_') === slugCamp);
                          const t = normalizeInputType(customDef ? (customDef.type ?? customDef.tip) : (camp.tip ?? 'text'));
                          return (t === 'datetime-local' || t === 'date' || t === 'time') ? '' : `Introdu ${camp.label.toLowerCase()}`;
                        })()}
                        className="w-full pl-12 pr-5 py-4 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all outline-none text-gray-800 placeholder-gray-400"
                        style={{ '--tw-ring-color': `${culoareCTA}20`, borderColor: 'transparent' }}
                        onFocus={e => { e.target.style.borderColor = culoareCTA; e.target.parentElement.querySelector('svg').style.color = culoareCTA; }}
                        onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.parentElement.querySelector('svg').style.color = '#9ca3af'; }}
                      />
                    </div>
                  </div>
                ))}

                {customFields.map((field, index) => (
                  <div key={`custom-${index}`} className="group relative">
                    <label className="block text-sm font-bold mb-2 ml-1" style={{ color: culoareTitlu }}>
                      {field.label}
                      {field.required && <span className="text-red-500"> *</span>}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        required={field.required}
                        value={customData[field.label] || ''}
                        onChange={e => setCustomData({ ...customData, [field.label]: e.target.value })}
                        placeholder={`Completează ${field.label.toLowerCase()}`}
                        className="w-full px-5 py-4 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:ring-4 transition-all outline-none text-gray-800"
                        style={{ '--tw-ring-color': `${culoareCTA}20` }}
                        onFocus={e => e.target.style.borderColor = culoareCTA}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                        rows={3}
                      />
                    ) : (
                      (() => {
                        // Editor salvează tipul în "type"; alte surse pot folosi "tip" (aliniere cu camp.tip din același formular)
                        const inputTypeValidat = normalizeInputType(field.type ?? field.tip);
                        const isDateLike = inputTypeValidat === 'datetime-local' || inputTypeValidat === 'date' || inputTypeValidat === 'time';

                        return (
                      <input
                        type={inputTypeValidat}
                        required={field.required}
                        value={customData[field.label] || ''}
                        onChange={e => setCustomData({ ...customData, [field.label]: e.target.value })}
                        placeholder={isDateLike ? '' : `Completează ${field.label.toLowerCase()}`}
                        className="w-full px-5 py-4 bg-gray-50/50 border border-gray-200 rounded-xl focus:bg-white focus:ring-4 transition-all outline-none text-gray-800"
                        style={{ '--tw-ring-color': `${culoareCTA}20` }}
                        onFocus={e => e.target.style.borderColor = culoareCTA}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                      />
                        );
                      })()
                    )}
                  </div>
                ))}

                {landingPage?.allowAttachments && (
                  <div className="group p-6 border-2 border-dashed border-gray-200 rounded-2xl hover:border-brand-blue transition-colors bg-gray-50/30">
                    <label className="block text-sm font-bold mb-3" style={{ color: culoareTitlu }}>
                      Încarcă Documente (Opțional)
                    </label>
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-32 cursor-pointer">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <span className="text-4xl mb-2">📂</span>
                          <p className="mb-1 text-sm text-gray-500"><span className="font-semibold">Apasă pentru a încărca</span></p>
                          <p className="text-xs text-gray-400">PDF, PNG, JPG (max 5MB)</p>
                        </div>
                        <input
                          type="file"
                          multiple
                          accept="image/*,.pdf"
                          onChange={e => setFiles(Array.from(e.target.files))}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {files.map((f, i) => (
                          <div key={i} className="flex items-center text-sm text-gray-600 bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                            <span className="mr-2">📎</span> {f.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm flex items-center shadow-sm">
                    <span className="mr-3 text-xl">⚠️</span> {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || submitLoading}
                  className="w-full py-4 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                  style={{ backgroundColor: culoareCTA }}
                >
                  {submitLoading ? '⏳ Se trimite...' : '✅ Trimite Solicitarea'}
                </button>
              </form>
            </div>
          ) : (
            <>
              {!success && (
                <div ref={formRef} className={`bg-white/95 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-2xl animate-fade-in-up border border-white/50 ring-1 ring-gray-100 ${centered ? 'max-w-xl mx-auto' : ''}`}>
                  <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6 text-4xl shadow-inner">📱</div>
                  <h3 className="text-3xl font-bold mb-4" style={{ color: culoareTitlu }}>Verifică telefonul</h3>
                  <p className="mb-8 text-lg" style={{ color: culoareDescriere }}>
                    Am trimis un cod de 6 cifre pe numărul <strong className="text-gray-900 bg-gray-100 px-2 py-1 rounded-md">{formData.telefon}</strong>.<br />
                    Introdu codul pentru a finaliza cererea.
                  </p>

                  <form onSubmit={handleSmsVerify} className="max-w-xs mx-auto">
                    <input
                      type="text"
                      maxLength="6"
                      value={smsCode}
                      onChange={e => setSmsCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="0 0 0 0 0 0"
                      className="w-full px-4 py-4 text-4xl font-bold text-center tracking-[0.5em] border-2 border-gray-100 rounded-2xl focus:border-brand-blue outline-none mb-8 text-gray-800 placeholder-gray-200 transition-all shadow-sm focus:shadow-md bg-white"
                      style={{ caretColor: culoareCTA }}
                      onFocus={e => e.target.style.borderColor = culoareCTA}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                      autoFocus
                    />

                    {smsError && (
                      <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm mb-6 flex items-center justify-center shadow-sm">
                        <span className="mr-2 text-xl">⚠️</span> {smsError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={smsLoading || smsCode.length !== 6}
                      className="w-full py-4 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      style={{ backgroundColor: culoareCTA }}
                    >
                      {smsLoading ? '⏳ Se verifică...' : '✅ Confirmă codul'}
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      ) : stage === 'gdpr' ? (
        <div className="flex flex-col animate-fade-in transition-all duration-500">
          <button type="button" onClick={() => setStage('lp')} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-2 mb-6 w-fit transition-colors font-medium">
            ← Înapoi
          </button>
          <div className={`flex flex-col max-w-md ${centered ? 'mx-auto' : ''} bg-white/40 p-5 rounded-2xl border border-white/50 shadow-sm`}>
            <div className="flex items-start gap-3 mb-4">
              <input id="gdpr-consent" type="checkbox" checked={gdprConsent} onChange={(e) => setGdprConsent(e.target.checked)} className="w-5 h-5 rounded mt-0.5" style={{ accentColor: culoareCTA }} />
              <label htmlFor="gdpr-consent" className="text-sm font-medium leading-tight cursor-pointer" style={{ color: culoareTitlu }}>
                Sunt de acord cu prelucrarea datelor conform <a href="/politica-confidentialitate" target="_blank" rel="noopener noreferrer" className="underline font-bold">Politica GDPR</a>.
              </label>
            </div>
            <button type="button" onClick={handleGdprContinue} disabled={!gdprConsent} className="w-full md:w-auto px-10 py-4 rounded-2xl text-lg font-bold text-white shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all" style={{ backgroundColor: culoareCTA }}>
              Continuă →
            </button>
          </div>
        </div>
      ) : (stage === 'lp' || stage === 'ad') ? (
        <div className="flex flex-col animate-fade-in transition-all duration-500">
          <div className={`text-center ${centered ? 'md:text-center' : 'md:text-left'} mb-12`}>
            {(() => {
              const cfg = getLpConfig();
              const hasLocal = cfg.localVideoUrl;
              const hasEmbed = cfg.adVideoUrl && getYouTubeEmbedUrl(cfg.adVideoUrl);
              const hasVideo = hasLocal || hasEmbed || (landingPage?.videoUrl && !landingPage?.localVideoUrl);
              if (!hasVideo) return null;
              return (
                <div className="mb-6 rounded-2xl overflow-hidden shadow-2xl aspect-video max-w-3xl mx-auto w-full">
                  {hasLocal ? (
                    <video src={cfg.localVideoUrl} controls autoPlay muted playsInline loop className="w-full h-full object-contain rounded-xl" title="Video" />
                  ) : hasEmbed ? (
                    <iframe width="100%" height="100%" src={getYouTubeEmbedUrl(cfg.adVideoUrl)} title="Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : landingPage?.videoUrl ? (
                    <iframe width="100%" height="100%" src={landingPage.videoUrl.replace('watch?v=', 'embed/').replace('vimeo.com', 'player.vimeo.com/video')} title="Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : null}
                </div>
              );
            })()}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight tracking-tight" style={{ color: culoareTitlu }}>{landingPage?.titlu}</h1>
            {landingPage?.descriere && (
              <div className={`text-lg md:text-xl leading-relaxed max-w-2xl mx-auto prose prose-slate ${!centered ? 'md:mx-0' : ''}`} style={{ color: culoareDescriere, marginLeft: centered ? 'auto' : '0' }} dangerouslySetInnerHTML={{ __html: landingPage.descriere }} />
            )}
          </div>
          {bulletPoints.length > 0 && (
            <div className={`grid ${centered ? 'max-w-3xl mx-auto' : ''} md:grid-cols-2 gap-6 mb-12`}>
              {bulletPoints.map((bullet, index) => (
                <div key={index} className="flex items-start p-5 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/60 shadow-sm hover:shadow-md transition-all hover:bg-white">
                  <div className="flex-shrink-0 mr-4 mt-1">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-green-100 text-green-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                    </div>
                  </div>
                  <span className="text-lg font-medium leading-relaxed" style={{ color: culoareTitlu }}>{bullet.replace(/^✅\s*/, '')}</span>
                </div>
              ))}
            </div>
          )}
          <div className={`text-center ${centered ? '' : 'md:text-left'}`}>
            <button onClick={handleCTAClick} className="group relative w-full md:w-auto px-10 py-5 text-white rounded-2xl text-xl font-bold shadow-xl transition-all transform overflow-hidden hover:shadow-2xl hover:-translate-y-1 active:scale-95" style={{ backgroundColor: culoareCTA, boxShadow: `0 20px 40px -10px ${culoareCTA}60` }}>
              <div className="absolute inset-0 bg-white/20 group-hover:bg-transparent transition-colors duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              <span className="relative flex items-center justify-center gap-3">
                <span>🎯</span>
                {landingPage?.textButonCTA || 'Solicită ofertă acum!'}
              </span>
            </button>
          </div>
        </div>
      ) : null}

      <div className="text-center mt-12 text-gray-400 text-sm font-medium">
        Powered by roasigur.ro
      </div>
    </>
  );

  const layout = lpConfig.layout || 'classic';

  // --- RENDER LAYOUTS (din noul Editor: classic, split, minimal, card; după ad rămâne conținutul) ---

  if (layout === 'split') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 sm:p-8" style={{ fontFamily }}>
        <div className="max-w-7xl w-full rounded-[2rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row min-h-[85vh]">
          {/* Stânga: fundal culoare + titlu/subtitlu */}
          <div className="w-full lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center order-2 lg:order-1 text-white" style={{ backgroundColor: lpConfig.culoare }}>
            <div className="lg:hidden mb-6">{renderAgentBadge(false)}</div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight" style={{ color: 'white' }}>
              {landingPage?.titlu}
            </h1>
            {landingPage?.descriere && (
              <div className="text-white/90 text-lg leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: landingPage.descriere }} />
            )}
          </div>
          {/* Dreapta: formular + conținut */}
          <div className="w-full lg:w-1/2 p-8 lg:p-12 relative flex flex-col justify-center order-1 lg:order-2 overflow-y-auto bg-white">
            {renderAgentBadge(true)}
            {renderContent(false)}
          </div>
        </div>
        <style>{`
          @keyframes fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
        `}</style>
      </div>
    );
  }

  if (layout === 'minimal') {
    return (
      <div className="min-h-screen py-12 transition-colors duration-500" style={{ backgroundColor: culoareFundal, fontFamily }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
          {renderContent(true)}
        </div>
        <style>{`
          @keyframes fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
        `}</style>
      </div>
    );
  }

  if (layout === 'card') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 py-12" style={{ fontFamily, background: `linear-gradient(135deg, ${lpConfig.culoare}ee, ${lpConfig.culoare}99)` }}>
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-8 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold mb-2" style={{ color: culoareTitlu }}>{landingPage?.titlu}</h1>
            {landingPage?.descriere && (
              <div className="text-lg leading-relaxed" style={{ color: culoareDescriere }} dangerouslySetInnerHTML={{ __html: landingPage.descriere }} />
            )}
          </div>
          {renderContent(true)}
        </div>
        <style>{`
          @keyframes fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
        `}</style>
      </div>
    );
  }

  // Classic (sau după 'ad': conținut cu header colorat, aliniat cu Editor)
  return (
    <div className="min-h-screen transition-colors duration-500" style={{ backgroundColor: culoareFundal, fontFamily }}>
      {/* Bandă header cu culoare din config */}
      <div className="w-full text-white text-center py-8 md:py-10 px-4" style={{ backgroundColor: lpConfig.culoare }}>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight">{landingPage?.titlu}</h1>
      </div>

      {(landingPage?.imaginePrincipala) && (
        <div className="w-full bg-slate-100 flex justify-center border-b border-slate-200 relative">
          <img
            src={landingPage.imaginePrincipala.startsWith('http') ? landingPage.imaginePrincipala : `${API_BASE_URL}${landingPage.imaginePrincipala}`}
            alt={landingPage.titlu}
            className="w-full max-w-7xl h-auto max-h-[30vh] object-contain my-4 drop-shadow-xl"
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {renderContent(true)}
      </div>

      <style>{`
        @keyframes fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default PublicLandingPage;
